import { useMemo, useEffect, useRef } from 'react';
import { Dropdown, Field, Form, TextInput, useFormState, useFormApi } from '@carboledger/ui';
import { buildUnitOptions, hasMaterialDensity } from '@/lib/mappers';
import { useTranslations } from '@/hooks';
import { buildOrderQuantityRules } from '../ordersValidation';

/**
 * Props:
 *   materials       material view models, for the units and the conversion
 *   isLocked        something is shipped or allocated: Unit stays locked, only the quantity may rise
 *   shippedByUnit   unit symbol → already-shipped quantity, the entry's floor
 *   allocatedByUnit unit symbol → already-allocated quantity, the entry's floor
 *   children        Tolerance and Dispatch by — the row's other two cells
 */
export const OrderQuantityFields = ({
  materials,
  isLocked = false,
  shippedByUnit = null,
  allocatedByUnit = null,
  children,
}) => {
  const t = useTranslations();
  const { values } = useFormState();
  const { setValue } = useFormApi();

  const selectedMaterial = useMemo(
    () => materials.find((material) => material.id === values.materialId) ?? null,
    [materials, values.materialId],
  );

  const unitBadges = useMemo(
    () => ({ working: t('UNIT_WORKING'), declaration: t('UNIT_DECLARATION') }),
    [t],
  );

  // Without a conversion factor nothing can turn a working quantity into declaration, so Working isn't offered.
  const unitOptions = useMemo(() => {
    const options = buildUnitOptions(selectedMaterial, unitBadges);
    if (hasMaterialDensity(selectedMaterial)) return options;
    return options.filter((option) => option.badgeText !== unitBadges.working);
  }, [selectedMaterial, unitBadges]);

  // Only once a material resolves — otherwise this would wipe Edit's prefilled display unit.
  const displayUnitId = selectedMaterial?.displayUnitId ?? '';
  useEffect(() => {
    if (!selectedMaterial) return;
    if (String(values.displayUnitId) !== String(displayUnitId)) {
      setValue('displayUnitId', displayUnitId);
    }
  }, [selectedMaterial, displayUnitId, values.displayUnitId, setValue]);

  // A unit the current material doesn't offer can't stay selected.
  useEffect(() => {
    if (values.unit && !unitOptions.some((option) => option.value === values.unit)) {
      setValue('unit', '');
    }
  }, [unitOptions, values.unit, setValue]);

  // No working-unit correlation for this material: Declaration unit is the only option, so it is the default, not a manual pick.
  useEffect(() => {
    if (!values.unit && unitOptions.length === 1) {
      setValue('unit', unitOptions[0].value);
    }
  }, [unitOptions, values.unit, setValue]);

  const materialDensity = hasMaterialDensity(selectedMaterial)
    ? String(selectedMaterial.density)
    : null;

  // Guarded so mount doesn't discard a saved order's density.
  const previousMaterialId = useRef(values.materialId);
  useEffect(() => {
    if (previousMaterialId.current === values.materialId) return;
    previousMaterialId.current = values.materialId;
    setValue('density', materialDensity ?? '');
  }, [values.materialId, materialDensity, setValue]);

  useEffect(() => {
    if (!materialDensity) return;
    if (values.density !== materialDensity) setValue('density', materialDensity);
  }, [materialDensity, values.density, setValue]);

  const quantityRules = useMemo(
    () =>
      buildOrderQuantityRules({
        shippedMin: shippedByUnit?.[values.unit] ?? null,
        allocatedMin: allocatedByUnit?.[values.unit] ?? null,
        unit: values.unit,
      }),
    [shippedByUnit, allocatedByUnit, values.unit],
  );

  return (
    <Form.Row columns={4}>
      <Field name="contractedQuantity" required rules={quantityRules}>
        {({ id, name, value, onChange, onBlur, status, message, required }) => (
          <TextInput
            id={id}
            name={name}
            label={t('FIELD_CONTRACTED_QUANTITY')}
            placeholder={t('CONTRACTS_FORM_CONTRACTED_QUANTITY_PLACEHOLDER')}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            status={status}
            message={message}
            required={required}
          />
        )}
      </Field>

      <Field name="unit" required>
        {({ id, name, value, onChange, onBlur, status, message, required }) => (
          <Dropdown
            id={id}
            name={name}
            label={t('FIELD_UNIT')}
            placeholder={t('FORM_SELECT_UNIT')}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            status={status}
            message={message}
            required={required}
            disabled={isLocked}
            options={unitOptions}
            emptyMessage={t('FORM_SELECT_MATERIAL_FIRST')}
          />
        )}
      </Field>

      {children}
    </Form.Row>
  );
};
